import React, { useState } from 'react';
import { SUPPORT_PHONE, SUPPORT_PHONE_TEL } from '../constants/contact';
import { Link } from 'react-router-dom';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Headphones,
  ArrowUpRight,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { ScrollReveal, ScrollRevealStagger, ScrollRevealItem } from './ScrollReveal';
import './contact.css';

const contactCards = [
  {
    icon: Phone,
    label: 'Call us',
    value: SUPPORT_PHONE,
    href: `tel:${SUPPORT_PHONE_TEL}`,
    accent: '#0F8F5E',
    cta: 'Call now',
  },
  {
    icon: Mail,
    label: 'Email us',
    value: 'info@credit99.com',
    href: 'mailto:info@credit99.com',
    accent: '#10273F',
    cta: 'Send email',
  },
  {
    icon: MapPin,
    label: 'Visit us',
    value: 'Office no. 304, Laxman Palace, Delhi',
    accent: '#C99A3F',
    cta: 'Get directions',
  },
];

const highlights = [
  { icon: Zap, text: 'Reply within 24 hours' },
  { icon: ShieldCheck, text: 'Secure & confidential' },
  { icon: Headphones, text: 'Dedicated loan support' },
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = `mailto:info@credit99.com?subject=Contact from ${encodeURIComponent(form.name)}&body=${encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\n${form.message}`
    )}`;
  };

  return (
    <section className="cu-section" id="contact-us">
      <div className="cu-bg" aria-hidden="true">
        <div className="cu-bg-gradient" />
        <div className="cu-bg-mesh" />
        <div className="cu-bg-dots" />
        <span className="cu-bg-orb cu-bg-orb--1" />
        <span className="cu-bg-orb cu-bg-orb--2" />
        <span className="cu-bg-orb cu-bg-orb--3" />
      </div>

      <div className="cu-wrap">
        <ScrollReveal className="cu-hero" y={32}>
          <span className="cu-eyebrow">Get in touch</span>
          <h1 className="cu-title">
            We&apos;re here to <span>help you</span>
          </h1>
          <p className="cu-lead">
            Questions about your loan, application status, or repayments — reach out
            and our team will guide you every step of the way.
          </p>
          <ul className="cu-highlights">
            {highlights.map(({ icon: Icon, text }) => (
              <li key={text}>
                <Icon size={16} strokeWidth={2.2} aria-hidden="true" />
                {text}
              </li>
            ))}
          </ul>
        </ScrollReveal>

        <ScrollReveal className="cu-shell" y={44} duration={0.6}>
          <aside className="cu-visual" aria-label="Contact channels">
            <div className="cu-visual-glow" aria-hidden="true" />
            <div className="cu-visual-ring" aria-hidden="true" />

            <div className="cu-visual-head">
              <span className="cu-visual-tag">Direct support</span>
              <h2>Reach us anytime</h2>
              <p>Mon–Sat · 10:00 AM – 7:00 PM</p>
            </div>

            <ScrollRevealStagger className="cu-cards" stagger={0.1}>
              {contactCards.map(({ icon: Icon, label, value, href, accent, cta }) => (
                <ScrollRevealItem key={label} className="cu-card" y={20}>
                  <div className="cu-card-top">
                    <span className="cu-card-icon" style={{ '--cu-accent': accent }}>
                      <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <span className="cu-card-label">{label}</span>
                  </div>
                  <p className="cu-card-value">{value}</p>
                  {href ? (
                    <a href={href} className="cu-card-link">
                      {cta}
                      <ArrowUpRight size={14} aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="cu-card-link cu-card-link--static">{cta}</span>
                  )}
                </ScrollRevealItem>
              ))}
            </ScrollRevealStagger>

            
          </aside>

          <div className="cu-form-side">
            <div className="cu-form-head">
              <span className="cu-form-badge">Write to us</span>
              <h2>Send a message</h2>
              <p>Share your query — we typically respond within one business day.</p>
            </div>

            <form className="cu-form" onSubmit={handleSubmit}>
              <div className="cu-field">
                <label htmlFor="cu-name">Full name</label>
                <input
                  id="cu-name"
                  name="name"
                  type="text"
                  placeholder="Enter your name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="cu-field-row">
                <div className="cu-field">
                  <label htmlFor="cu-email">Email address</label>
                  <input
                    id="cu-email"
                    name="email"
                    type="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="cu-field">
                  <label htmlFor="cu-phone">Phone number</label>
                  <input
                    id="cu-phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="cu-field">
                <label htmlFor="cu-message">Your message</label>
                <textarea
                  id="cu-message"
                  name="message"
                  rows={5}
                  placeholder="Tell us how we can help..."
                  value={form.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <button type="submit" className="cu-submit">
                Send message
                <Send size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </form>
          </div>
        </ScrollReveal>

        <ScrollReveal className="cu-map-block" delay={0.1} y={36}>
          <div className="cu-map-info">
            <span className="cu-map-tag">Our location</span>
            <h3>Registered office</h3>
            <p>Office no. 304, Laxman Palace, Delhi, India</p>
            <div className="cu-map-hours">
              <Clock size={15} strokeWidth={2.2} aria-hidden="true" />
              <span>Open Mon–Sat, 10 AM – 7 PM</span>
            </div>
            <a
              href="https://maps.google.com/?q=Laxman+Palace+Delhi"
              target="_blank"
              rel="noopener noreferrer"
              className="cu-map-link"
            >
              Open in Google Maps
              <ArrowUpRight size={14} aria-hidden="true" />
            </a>
          </div>
          <div className="cu-map">
            <iframe
              title="Credit99 office location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.761474122257!2d77.31459607528696!3d28.57692447569521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce45b22a79785%3A0xac760eff02b41b9a!2sWorld%20Trade%20Tower!5e0!3m2!1sen!2sin!4v1759321448244!5m2!1sen!2sin"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default Contact;
